import { cn } from '@/lib/utils';

/** 'all' | 'mine' | 'unassigned' | a principalId */
export type AssigneeFilterValue = string;

interface AssigneeFilterBarProps {
  value: AssigneeFilterValue;
  onChange: (value: AssigneeFilterValue) => void;
  /** Active members of the workspace, for per-member filtering. */
  members: Array<{ principalId: string; label: string }>;
  /** The caller's principalId — drives "My tasks" and is excluded from member chips. */
  myPrincipalId?: string | null;
  /**
   * Whether to show the "My tasks" chip. Defaults to true. On the list-detail
   * page this chip is the entry point to the per-user board overlay (the page
   * reads value === 'mine' and swaps the shared list for that board); on the
   * Completed timeline it's a plain assignee filter (no personal overlay for
   * done work).
   */
  showMine?: boolean;
}

/**
 * Filter the list by assignee. "Everyone" shows the full sectioned list;
 * picking a person (yourself, a teammate, or Unassigned) shows just their
 * tasks. Every member gets a chip per teammate to filter to that person's
 * workload, plus "My tasks" for their own.
 */
export function AssigneeFilterBar({
  value,
  onChange,
  members,
  myPrincipalId,
  showMine = true,
}: AssigneeFilterBarProps) {
  const chip = (key: string, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(key)}
      aria-pressed={value === key}
      className={cn(
        'px-2.5 py-1 rounded-full text-xs border transition-colors',
        value === key
          ? 'bg-primary text-primary-foreground border-primary'
          : 'border-border text-muted-foreground hover:bg-accent',
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-0.5 text-xs text-muted-foreground">Show:</span>
      {chip('all', 'Everyone')}
      {showMine && myPrincipalId && chip('mine', 'My tasks')}
      {chip('unassigned', 'Unassigned')}
      {members
        .filter((m) => m.principalId !== myPrincipalId)
        .map((m) => chip(m.principalId, m.label))}
    </div>
  );
}
